/**
 * Safely extract a human-readable error message from a FastAPI error response.
 *
 * FastAPI returns `detail` as EITHER:
 *   - a string:  { "detail": "Not found" }
 *   - an array:  { "detail": [{ "type":"missing", "loc":["body","name"], "msg":"Field required", "input":{}, "url":"..." }] }
 *
 * Rendering the array directly as JSX causes:
 *   "Objects are not valid as a React child"
 */
export function extractApiError(
  data: any,
  fallback: string = 'An error occurred',
): string {
  if (!data) return fallback;

  const detail = data.detail ?? data.error ?? data.message;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    // Pydantic validation errors: [{type, loc, msg, input, url}]
    const messages = detail
      .map((e: any) => {
        if (typeof e === 'string') return e;
        if (e?.msg) {
          const field = Array.isArray(e.loc) ? e.loc.filter((l: any) => l !== 'body').join('.') : '';
          return field ? `${field}: ${e.msg}` : e.msg;
        }
        return JSON.stringify(e);
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join('; ') : fallback;
  }

  if (typeof detail === 'object') {
    // Nested errors object: { "detail": { "errors": ["..."] } }
    if (Array.isArray(detail.errors)) return detail.errors.join('; ');
    return JSON.stringify(detail);
  }

  return fallback;
}
